<template>
  <v-dialog :value="isActive" persistent max-width="550" scrollable>
    <ValidationObserver v-slot="{ handleSubmit }">
      <v-card :loading="isLoading">
        <v-card-title class="pa-0 shadow-1">
          <v-img
            :src="require('@/assets/illustrations/bg_modal_card.svg')"
            cover
            position="left bottom"
            class="align-center"
            height="65px"
          >
            <v-card-title class="font-weight-medium primary--text">
              {{ isEditMode ? "Actualizar" : "Crear" }} texto clave
            </v-card-title>
          </v-img>
        </v-card-title>

        <vuescroll
          :ops="{
            bar: { background: '#bacfcec0', keepShow: true },
          }"
        >
          <v-card-text style="max-height: 450px">
            <v-row>
              <v-col cols="12" class="pb-0" v-if="whatsappLabel">
                <span class="subtitle-2 grey--text">Línea: {{ whatsappLabel }}</span>
              </v-col>

              <!-- Texto clave -->
              <v-col cols="12" class="pb-0">
                <ValidationProvider name="texto clave" rules="required|max:255" v-slot="{ errors }">
                  <v-text-field
                    v-model="payload.message"
                    :error-messages="errors"
                    label="Texto clave"
                    placeholder="Se compara exacto contra el mensaje recibido, ignorando mayúsculas y espacios"
                    rounded
                    class="rounded-small"
                    filled
                    hint="Se compara exacto contra el mensaje recibido, ignorando mayúsculas y espacios. Ej: ' AsESOR ' coincide con 'asesor', pero 'quiero un asesor' no."
                    persistent-hint
                  ></v-text-field>
                </ValidationProvider>
              </v-col>

              <!-- Cola destino -->
              <v-col cols="12" class="pb-0">
                <ValidationProvider name="cola destino" v-slot="{ errors }">
                  <v-select
                    v-model="payload.queue_id"
                    :items="queueList"
                    :error-messages="errors"
                    item-text="name"
                    item-value="id"
                    label="Cola destino (opcional)"
                    hint="Si no se selecciona, se usa la cola por defecto de la línea."
                    persistent-hint
                    clearable
                    rounded
                    class="rounded-small"
                    filled
                  >
                    <template v-slot:item="{ item, on, attrs }">
                      <v-list-item two-line selectable v-on="on" v-bind="attrs" class="pl-3">
                        <template>
                          <v-list-item-content>
                            <v-list-item-title>
                              {{ item.name + " " }}
                            </v-list-item-title>

                            <v-list-item-subtitle>
                              {{ item.number }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </template>
                      </v-list-item>
                    </template>
                  </v-select>
                </ValidationProvider>
              </v-col>

              <!-- Agente destino -->
              <v-col cols="12" class="pb-0">
                <ValidationProvider name="agente destino" v-slot="{ errors }">
                  <v-select
                    v-model="payload.user_id"
                    :items="userList"
                    :error-messages="errors"
                    item-text="name"
                    item-value="id"
                    label="Agente destino (opcional)"
                    hint="Si no se selecciona, el ticket queda pendiente hasta que un agente lo tome."
                    persistent-hint
                    clearable
                    rounded
                    class="rounded-small"
                    filled
                  ></v-select>
                </ValidationProvider>
              </v-col>
            </v-row>
          </v-card-text>
        </vuescroll>

        <v-card-actions>
          <v-btn @click="closeDialog()" class="px-8" color="error" rounded large text> Cancelar </v-btn>
          <v-spacer></v-spacer>

          <v-btn @click="handleSubmit(submitForm)" :disabled="isLoading" class="px-8" color="success" rounded large>
            {{ isEditMode ? "Actualizar" : "Crear" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </ValidationObserver>
  </v-dialog>
</template>

<script>
import axios from "axios";
import vuescroll from "vuescroll";
import { PbxHttpClient } from "@/services/httpclient";
import { extractApiError } from "@/utils";

export default {
  components: {
    vuescroll,
  },

  props: {
    isActive: {
      type: Boolean,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    dataToEdit: {
      type: Object,
      // eslint-disable-next-line vue/require-valid-default-prop
      default: {},
    },
    whatsappId: {
      type: [Number, String],
      default: null,
    },
    whatsappLabel: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      isLoading: false,
      queueList: [],
      userList: [],
      payload: {
        id: null,
        message: "",
        queue_id: null,
        user_id: null,
      },
    };
  },

  created() {
    this.getSelectsInfo();
    this.payload = {
      id: this.dataToEdit.id || null,
      message: this.dataToEdit.message || "",
      queue_id: (this.dataToEdit.queue && this.dataToEdit.queue.id) || this.dataToEdit.queue_id || null,
      user_id: (this.dataToEdit.user && this.dataToEdit.user.id) || this.dataToEdit.user_id || null,
    };
  },

  methods: {
    closeDialog(data = false) {
      this.$emit("close:dialog", data);
    },

    async getSelectsInfo() {
      let endpoints = ["/clients/queues", "/clients/show/agents_select"];

      await axios
        .all(endpoints.map((endpoint) => PbxHttpClient.get(endpoint)))
        .then((res) => {
          this.queueList = res[0].data.data;
          this.userList = res[1].data.data;
        })
        .catch(() => {
          this.$snotify.error("Hubo un error en la consulta.", "Error");
        });
    },

    async submitForm() {
      this.isLoading = true;

      try {
        const body = {
          message: this.payload.message,
          whatsapp_id: this.whatsappId,
          queue_id: this.payload.queue_id || null,
          user_id: this.payload.user_id || null,
        };

        const { data } = this.isEditMode
          ? await PbxHttpClient.put(`/whatsapps/keyword-redirections/${this.payload.id}`, body)
          : await PbxHttpClient.post("/whatsapps/keyword-redirections", body);

        this.closeDialog(data);

        this.$snotify.success(`Texto clave ${this.isEditMode ? "actualizado" : "creado"} con éxito.`, "¡Hecho!");
      } catch (err) {
        this.$snotify.error(extractApiError(err, "No se pudo realizar la acción."), "¡Error!");
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
